import React, {useState} from 'react';
import { useTheme } from '@mui/joy';
import { useSelector } from 'react-redux';
import Autocomplete from 'components/core/Autocomplete';
import SearchAutocomplete from 'components/SearchAutocomplete';
import Typography from 'components/core/Typography';
import Stack from 'components/core/Stack';
import Card from 'components/core/Card';

import { lookupTableClasses } from 'utils/LookupTableUtils';
import lookupTableActions from 'actions/lookupTableActions';
import taxonomyNameActions from 'actions/taxonomyNameActions'

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';

import PersonForm from './PersonForm';
import LookupTableForm from './LookupTableForm';
import EventForm from './EventForm';
import LocationForm from './LocationForm';


const LookupTableInput = ({
    label,
    value,
    onChange,
    options,
    getOptionLabel,
    suppressForm,
    error,
    warning,
    ...props
}) => {
    const theme = useTheme();
    const [showSubform, setShowSubform] = useState(false);
    const me = useSelector(({ profile }) => profile.me);

    let codeDescription
    let examples

    const handleChange = (value) => {
        if (value?.isNoOption) {
            setShowSubform(true)
            return
        }
        onChange(value)
        setShowSubform(false)
    }

    try {
        codeDescription = value?.get('codeDescription')
        examples = value?.get('examples')
    } catch (error) {
    }

    let className
    if (options[0]?.className) {
        className = options[0]?.className
    }


    const subforms = [
        {
            className: 'PersonName',
            classNameLabel: 'Person',
            component: PersonForm,
            mustBeAdmin: false,
            sortField: 'custom'
        },
        ...lookupTableClasses.map(c => ({
            className: c.className,
            classNameLabel: c.classNameLabel,
            component: LookupTableForm,
            mustBeAdmin: true,
            sortField: 'label'
        })),
        {
            className: 'Event',
            classNameLabel: 'Event',
            component: EventForm,
            mustBeAdmin: true,
            sortField: 'name'
        },
        {
            className: 'Location',
            classNameLabel: 'Location',
            component: LocationForm,
            mustBeAdmin: true,
            sortField: 'label'
        },
        {
            className: 'Subject',
            classNameLabel: 'Subject',
            sortField: 'shortCode'
        },
    ]

    const subform = subforms.find(subform => subform.className === className)
    const hasAddOption = !(!subform || subform.mustBeAdmin && me?.type !== 'ADMIN' || !subform.component || suppressForm)

    const handleSubmitSubform = (subformObject) => {
        if (props.multiple) {
            onChange([...value, subformObject])
        } else {
            onChange(subformObject)
        }
        setShowSubform(false)
    }

    const noOptionComponent = () => {
        if (!hasAddOption) {
            return <li>No options</li>
        }
        return (
            <li onClick={() => { setShowSubform(true) }} style={{cursor: 'pointer'}}>
                <Stack direction="row">
                    <AddCircleOutlineIcon sx={{marginRight: 2, color: 'black'}}/>
                    <Typography>Add New {subform?.classNameLabel}</Typography>
                </Stack>
            </li>
        )
    }

    const renderSubform = () => {
        if (!showSubform) return null
    
        const subform = subforms.find(subform => subform.className === className)
        if (!subform || subform.mustBeAdmin && me?.type !== 'ADMIN') return null
        return (
            <Card sx={{background: theme.palette.primary[50]}}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 2}}>
                    <Typography variant="h4">Create {subform?.classNameLabel}</Typography>
                    <CloseIcon onClick={() => setShowSubform(false)} sx={{cursor: 'pointer'}} />
                </Stack>
                <subform.component onSubmit={handleSubmitSubform} className={subform.className} />
            </Card>
        )
    }

    const descriptionCard = () => {
        if (!codeDescription && !examples) return null

        return (
            <Card sx={{background: theme.palette.primary[50]}}>
                <Stack spacing={2} direction="row" alignItems="center">
                    <InfoOutlinedIcon sx={{color: theme.palette.primary.main}}/>
                    <Stack spacing={2}>
                        {codeDescription && (
                            <Typography variant="caption">{codeDescription}</Typography>
                        )}
                        {examples && (
                            <Typography variant="caption" sx={{ml: 1}}>Examples: {examples}</Typography>
                        )}
                    </Stack>
                </Stack>
            </Card>
        );
    }


    const handleOptionLabel = (option) => {
        if (option.isNoOption) {
            return option.label
        }
        if (subform?.className === 'PersonName') {
            const person = option.get('person');
            const personOfficialName = `${person?.get('firstName')} ${person?.get('lastName')}`; 
            const thisName = `${option?.get('name')}`;
            if (personOfficialName === thisName) {
                return personOfficialName;
            } else {
                return `${personOfficialName} (${thisName})`
            }
        }
        return getOptionLabel(option)
    }

    const sortOptions = (a, b) => {
        if (subform?.sortField === 'custom') {
            if (subform.className === 'PersonName') {
                return a.get('person').get('lastName') < b.get('person').get('lastName') ? -1 : 1
            }
        }
        const sortFiels = subform?.sortField || 'label'
        if (a.isNoOption) {
            return -1
        }
        if (b.isNoOption) {
            return 1
        }
        return a.get(sortFiels) < b.get(sortFiels) ? -1 : 1
    }

    const sortedOptions = options.sort(sortOptions)

    
    const renderInput = () => {
        if (className === 'Location') {
            return (
                <SearchAutocomplete
                    label={label}
                    value={value}
                    onSelect={handleChange}
                    searchAction={(searchTerm, limit, skip) => lookupTableActions.searchLookupTable('Location', searchTerm, limit, skip)}
                    selector={({ lookupTable }) => lookupTable.searchState?.Location?.searchResults || []}
                    isSearchingSelector={({ lookupTable }) => lookupTable.searchState?.Location?.isSearching}
                    getOptionLabel={handleOptionLabel}
                    noOptionComponent={noOptionComponent()}
                    noOptionAlwaysVisible={hasAddOption}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    error={error}
                    warning={warning}
                    {...props}
                />
            );
        } else if (className === 'Name') {
            return (
                <SearchAutocomplete
                    label={label}
                    value={value}
                    onSelect={handleChange}
                    searchAction={taxonomyNameActions.search}
                    selector={({ taxonomyName }) => taxonomyName?.searchResults || []}
                    isSearchingSelector={({ taxonomyName }) => taxonomyName?.isSearching}
                    getOptionLabel={handleOptionLabel}
                    noOptionComponent={noOptionComponent()}
                    noOptionAlwaysVisible={hasAddOption}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    error={error}
                    warning={warning}
                    {...props}
                />
            );
        } else if (className === 'PersonName') {
            return (
                <SearchAutocomplete
                    label={label}
                    value={value}
                    onSelect={handleChange}
                    searchAction={(searchTerm, limit, skip) => lookupTableActions.searchLookupTable('PersonName', searchTerm, limit, skip)}
                    selector={({ lookupTable }) => lookupTable.searchState?.PersonName?.searchResults || []}
                    isSearchingSelector={({ lookupTable }) => lookupTable.searchState?.PersonName?.isSearching}
                    getOptionLabel={handleOptionLabel}
                    noOptionComponent={noOptionComponent()}
                    noOptionAlwaysVisible={hasAddOption}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    error={error}
                    warning={warning}
                    {...props}
                />
            );
        }
        
        return (
            <Autocomplete
                {...props}
                noOptionComponent={noOptionComponent()}
                label={label}
                value={value}
                onChange={handleChange}
                options={sortedOptions}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                getOptionLabel={handleOptionLabel}
                error={error}
                warning={warning}
                noOptionAlwaysVisible={hasAddOption}
            />
        );
    };

    return (
        <Stack spacing={2}>
            {!showSubform && renderInput()}
            {descriptionCard()}
            {renderSubform()}
        </Stack>
    );
}

export default LookupTableInput;